/**
 * Original line icons for the core area (24×24 grid, 1.8px stroke, currentColor).
 * Icons are decorative: they are always aria-hidden and paired with visible or sr-only text.
 */
import type { ReactNode, SVGProps } from "react";

export type IconName =
  | "home"
  | "sports"
  | "horse"
  | "numbers"
  | "live"
  | "casino"
  | "user"
  | "shield"
  | "wallet"
  | "plus"
  | "menu"
  | "close"
  | "chevron-down"
  | "chevron-right"
  | "check"
  | "clock"
  | "lock"
  | "info"
  | "alert"
  | "copy"
  | "download"
  | "phone"
  | "message"
  | "logout"
  | "external"
  | "arrow-right"
  | "help"
  | "pause"
  | "sliders"
  | "chart"
  | "receipt"
  | "id"
  | "key"
  | "bank"
  | "scale"
  | "doc"
  | "user-x"
  | "eye"
  | "eye-off"
  | "refresh"
  | "tv"
  | "chip"
  | "slots"
  | "rocket"
  | "cards"
  | "bolt"
  | "ticket"
  | "heart"
  | "heart-fill"
  | "chevron-left"
  | "trophy"
  | "upload"
  | "camera"
  | "flame"
  | "avatar"
  | "whatsapp";

const PATHS: Record<IconName, ReactNode> = {
  horse: <><path d="m6 20 2-7-4-2 3-5 3 1 2-4 4 2 3 6-3 2-1 7M8 13l7 1M9 7l4 4M12 4l-1-2M16 7h.01"/><path d="M4 20h16"/></>,
  numbers: <><circle cx="8" cy="15" r="5"/><circle cx="16" cy="15" r="5"/><circle cx="12" cy="6" r="4"/><path d="M7 13h2l-1 4M15 13h2l-2 4h2M11 5h2l-2 2h2"/></>,
  home: (
    <>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v9.5h13V10" />
      <path d="M10 19.5v-5h4v5" />
    </>
  ),
  sports: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m12 8 3.3 2.4-1.3 3.9H10l-1.3-3.9z" />
      <path d="M12 3.5V8M15.3 10.4l4-1.3M14 14.3l2.5 3.4M10 14.3l-2.5 3.4M8.7 10.4l-4-1.3" />
    </>
  ),
  live: <path d="M3 12h4l2.5-6 5 12 2.5-6H21" />,
  casino: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3.5" />
      <circle cx="9" cy="9" r="1.1" fill="currentColor" />
      <circle cx="15" cy="9" r="1.1" fill="currentColor" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" />
      <circle cx="9" cy="15" r="1.1" fill="currentColor" />
      <circle cx="15" cy="15" r="1.1" fill="currentColor" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.8" />
      <path d="M4.5 20c1.2-3.8 4.2-5.6 7.5-5.6s6.3 1.8 7.5 5.6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.5 19 6v5.5c0 4.3-2.9 7.6-7 9-4.1-1.4-7-4.7-7-9V6z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),
  wallet: (
    <>
      <path d="M4 8V7a2 2 0 0 1 2-2h11v3" />
      <rect x="4" y="8" width="16.5" height="11" rx="2.5" />
      <circle cx="16" cy="13.5" r="1.1" fill="currentColor" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  "chevron-right": <path d="m9 6 6 6-6 6" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" />
      <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5" />
      <circle cx="12" cy="7.8" r="0.9" fill="currentColor" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 21 19.5H3z" />
      <path d="M12 10v4.5" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v11" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M5 19.5h14" />
    </>
  ),
  phone: <path d="M6.5 4h3l1.5 4-2 1.3a10 10 0 0 0 5.7 5.7l1.3-2 4 1.5v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4.5 6.2 2 2 0 0 1 6.5 4z" />,
  message: <path d="M5 5.5h14a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5h-9l-4.5 3v-3H5A1.5 1.5 0 0 1 3.5 16V7A1.5 1.5 0 0 1 5 5.5z" />,
  logout: (
    <>
      <path d="M14 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5H14" />
      <path d="M10.5 12H20M16.5 8.5 20 12l-3.5 3.5" />
    </>
  ),
  external: (
    <>
      <path d="M14 4.5h5.5V10" />
      <path d="M19.5 4.5 11 13" />
      <path d="M18 14v4a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 5 18V8a1.5 1.5 0 0 1 1.5-1.5H10" />
    </>
  ),
  "arrow-right": <path d="M5 12h14M13.5 6.5 19 12l-5.5 5.5" />,
  help: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.6 9.5a2.5 2.5 0 1 1 3.6 2.3c-.8.4-1.2 1-1.2 1.9v.3" />
      <circle cx="12" cy="16.8" r="0.9" fill="currentColor" />
    </>
  ),
  pause: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M10 9v6M14 9v6" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 7h9M17 7h3M4 12h3M11 12h9M4 17h11M19 17h1" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="17" r="2" />
    </>
  ),
  chart: (
    <>
      <path d="M4 19.5h16" />
      <path d="M7 16v-4M11 16V8M15 16v-6M19 16V5.5" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3.5h12v17l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 11.5h6M9 15h3" />
    </>
  ),
  id: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M6 15.5c.6-1.3 1.7-2 3-2s2.4.7 3 2M14 10h4M14 13h3" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="3.5" />
      <path d="m10.5 12.5 8-8M16 7l2 2M13.5 9.5l1.5 1.5" />
    </>
  ),
  bank: (
    <>
      <path d="M3.5 9 12 4.5 20.5 9" />
      <path d="M5.5 10v7.5M10 10v7.5M14 10v7.5M18.5 10v7.5M3.5 19.5h17" />
    </>
  ),
  scale: (
    <>
      <path d="M12 4.5v15M8 19.5h8M5 7.5h14" />
      <path d="m5 7.5-2.5 6a2.8 2.8 0 0 0 5 0z" />
      <path d="m19 7.5-2.5 6a2.8 2.8 0 0 0 5 0z" />
    </>
  ),
  doc: (
    <>
      <path d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5H7a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z" />
      <path d="M13.5 3.5V8H18M9.5 12h5M9.5 15.5h5" />
    </>
  ),
  "user-x": (
    <>
      <circle cx="10" cy="8.5" r="3.6" />
      <path d="M3.5 20c1.1-3.6 3.6-5.4 6.5-5.4 1.4 0 2.7.4 3.8 1.2M16 14l4.5 4.5M20.5 14 16 18.5" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  "eye-off": (
    <>
      <path d="M4 4l16 16" />
      <path d="M9.9 6A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17.3 17.3 0 0 1-2.9 3.7M6.3 7.6A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9.2 9.2 0 0 0 4.2-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </>
  ),
  refresh: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" />
      <path d="M19.5 4.5v4h-4" />
    </>
  ),
  tv: (
    <>
      <rect x="3" y="6" width="18" height="12.5" rx="2" />
      <path d="M8.5 3.5 12 6l3.5-2.5M8 21h8" />
      <path d="m10.5 9.5 4 2.75-4 2.75z" fill="currentColor" />
    </>
  ),
  chip: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 3.5v3.3M12 17.2v3.3M3.5 12h3.3M17.2 12h3.3M6 6l2.3 2.3M15.7 15.7 18 18M18 6l-2.3 2.3M8.3 15.7 6 18" />
    </>
  ),
  slots: (
    <>
      <rect x="3" y="5" width="15" height="15" rx="2.2" />
      <path d="M8 9v7M13 9v7M3 9h15M3 16h15" />
      <path d="M18 11h1.5a1.5 1.5 0 0 0 1.5-1.5V6" />
      <circle cx="21" cy="5" r="1.2" fill="currentColor" />
    </>
  ),
  rocket: (
    <>
      <path d="M14.5 4.5c2.6-.9 4.6-1 5-.6.4.4.3 2.4-.6 5L12 16.3 7.7 12z" />
      <path d="M9.5 10.2 6 9.8 3.5 12.3l4.2 1.2M13.8 14.5l.4 3.5-2.5 2.5-1.2-4.2" />
      <circle cx="15.6" cy="8.4" r="1.4" />
      <path d="M6.5 17.5 4 20" />
    </>
  ),
  cards: (
    <>
      <rect x="3.5" y="6.5" width="10" height="14" rx="1.6" transform="rotate(-10 8.5 13.5)" />
      <rect x="10" y="4" width="10" height="14" rx="1.6" transform="rotate(8 15 11)" />
      <path d="M15.2 8.4c-1.4 1-2.2 1.9-2.2 2.9a1.3 1.3 0 0 0 2.2 1 1.3 1.3 0 0 0 2.2-1c0-1-.8-1.9-2.2-2.9z" fill="currentColor" />
    </>
  ),
  bolt: <path d="M13.5 3 5 13.5h6.2L10.5 21 19 10.5h-6.2z" />,
  ticket: (
    <>
      <path d="M4 6.5h16v3.2a2.3 2.3 0 0 0 0 4.6v3.2H4v-3.2a2.3 2.3 0 0 0 0-4.6z" />
      <path d="M9.5 9.5h5M9.5 12h5M9.5 14.5h3" />
    </>
  ),
  heart: <path d="M12 19.5s-7.5-4.4-7.5-9.7A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 7.5 2.8c0 5.3-7.5 9.7-7.5 9.7z" />,
  "heart-fill": <path d="M12 19.5s-7.5-4.4-7.5-9.7A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 7.5 2.8c0 5.3-7.5 9.7-7.5 9.7z" fill="currentColor" />,
  "chevron-left": <path d="m15 6-6 6 6 6" />,
  trophy: (
    <>
      <path d="M8 4.5h8v5a4 4 0 0 1-8 0z" />
      <path d="M8 6.5H5v1.5a3 3 0 0 0 3 3M16 6.5h3v1.5a3 3 0 0 1-3 3M12 13.5v3.5M8.5 20h7M9.5 17h5v3h-5z" />
    </>
  ),
  upload: (
    <>
      <path d="M12 15.5V4.5" />
      <path d="m7.5 9 4.5-4.5L16.5 9" />
      <path d="M5 15v3.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2.3l1.4-2h5.6l1.4 2h2.3A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z" />
      <circle cx="12" cy="12.8" r="3.3" />
    </>
  ),
  flame: <path d="M12 21c-3.9 0-6.5-2.6-6.5-6.1 0-3.4 2.5-5.4 3.7-8.4.3 1.8 1.2 3 2.4 3.6-.3-2.9.9-5.6 3.4-7.1-.3 2.9 1.2 4.6 2.6 6.4 1.2 1.6 1.9 3.2 1.9 5.2 0 3.6-3 6.4-7.5 6.4z" fill="currentColor" stroke="none" />,
  avatar: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="10" r="3.2" fill="currentColor" stroke="none" />
      <path d="M6.3 18.4c1.2-2.3 3.2-3.5 5.7-3.5s4.5 1.2 5.7 3.5" fill="currentColor" stroke="none" />
    </>
  ),
  whatsapp: (
    <>
      <path d="M4.5 19.5 5.6 16A8 8 0 1 1 8.4 18.6z" />
      <path d="M9.2 8.7c.3-.5.7-.5 1-.4l.7 1.6c.1.3 0 .5-.2.8l-.4.5c.5 1 1.4 1.9 2.5 2.5l.5-.4c.3-.2.5-.3.8-.2l1.6.7c.2.3.1.7-.4 1-1 .7-2.4.6-4-.5a9 9 0 0 1-2.7-2.7c-1-1.6-1.1-2.9-.4-3.9z" fill="currentColor" stroke="none" />
    </>
  ),
};

export function Icon({ name, size = 20, className, ...rest }: { name: IconName; size?: number } & Omit<SVGProps<SVGSVGElement>, "name">) {
  return (
    <svg
      className={className ? `wb-icon ${className}` : "wb-icon"}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
